import { AUDIO_SAMPLE_RATE, encodePcm16, hasVoice } from './pcm';

/**
 * Microphone capture as 24 kHz mono PCM16 frames.
 *
 * An `AudioWorkletNode` rather than `MediaRecorder`: `useVoiceInput` records
 * webm/opus blobs for push-to-talk transcription, but a live call needs raw
 * frames arriving continuously so GPT-Live can run turn detection on them. It is also a worklet rather than the deprecated `ScriptProcessorNode`
 * because that one runs on the main thread, where a React render can stall it into
 * a dropout mid-sentence.
 *
 * The processor is delivered as a Blob URL. `addModule` takes a URL, and the module
 * it loads runs in a separate realm the bundler does not participate in; a Blob keeps
 * the whole thing in one file with no build configuration and no separate asset to
 * keep in step.
 */

/** ~85 ms at 24 kHz. Big enough that per-message overhead is irrelevant, small
 * enough that it does not add audible latency to barge-in detection. */
const FRAME_SAMPLES = 2048;

const PROCESSOR_NAME = 'voice-call-pcm-capture';

const PROCESSOR_SOURCE = `
class VoiceCallPcmCapture extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this._frameSamples = options.processorOptions.frameSamples;
    this._buffer = new Float32Array(this._frameSamples);
    this._filled = 0;
  }

  // Called every 128 samples; accumulate into frame-sized chunks so the main
  // thread gets one message per frame rather than one per render quantum.
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (!channel) return true;
    let read = 0;
    while (read < channel.length) {
      const take = Math.min(this._frameSamples - this._filled, channel.length - read);
      this._buffer.set(channel.subarray(read, read + take), this._filled);
      this._filled += take;
      read += take;
      if (this._filled === this._frameSamples) {
        // Copy: the buffer is reused for the next frame, and a transfer would
        // detach it out from under this processor.
        this.port.postMessage(this._buffer.slice(0));
        this._filled = 0;
      }
    }
    return true;
  }
}

registerProcessor(${JSON.stringify(PROCESSOR_NAME)}, VoiceCallPcmCapture);
`;

export type MicCaptureOptions = {
  /** Called with each base64 PCM16 frame, in order. */
  onFrame: (base64: string) => void;
  /** Called with each frame loud enough to be the caller speaking. */
  onVoice?: () => void;
};

/** An open microphone. Every field is torn down by {@link MicCapture.stop}. */
export class MicCapture {
  private context: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private worklet: AudioWorkletNode | null = null;
  private moduleUrl: string | null = null;
  /** Set by {@link MicCapture.stop}; a start still awaiting permission or the
   * worklet sees it and releases what it acquired instead of going live. */
  private stopped = false;

  constructor(private readonly options: MicCaptureOptions) {}

  /** The open mic as an audio node, for visualizers. Null until started. */
  get input(): { context: AudioContext; node: AudioNode } | null {
    return this.context && this.source
      ? { context: this.context, node: this.source }
      : null;
  }

  /**
   * Ask for the microphone and start emitting frames.
   *
   * Throws whatever `getUserMedia` throws — `NotAllowedError` when the user
   * declines, `NotFoundError` when there is no input device. The caller renders
   * those; swallowing them here would leave a dead mic button with no explanation.
   * Stopped before it resolves, it releases the device and stays off.
   */
  async start(): Promise<void> {
    if (this.context || this.stopped) return;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    this.stream = stream;
    if (this.stopped) {
      this.stop();
      return;
    }
    // Constructing at the wire rate makes the browser resample once, in native
    // code, instead of us doing it per frame in JS.
    const context = new AudioContext({ sampleRate: AUDIO_SAMPLE_RATE });
    this.context = context;
    this.moduleUrl = URL.createObjectURL(
      new Blob([PROCESSOR_SOURCE], { type: 'application/javascript' }),
    );
    await context.audioWorklet.addModule(this.moduleUrl);
    if (this.stopped) {
      this.stop();
      return;
    }

    this.worklet = new AudioWorkletNode(context, PROCESSOR_NAME, {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      processorOptions: { frameSamples: FRAME_SAMPLES },
    });
    this.worklet.port.onmessage = (event: MessageEvent<Float32Array>) => {
      this.options.onFrame(encodePcm16(event.data));
      if (this.options.onVoice && hasVoice(event.data)) this.options.onVoice();
    };
    this.source = context.createMediaStreamSource(this.stream);
    this.source.connect(this.worklet);
    // Deliberately not connected to `context.destination`: the worklet has no
    // outputs, and routing the mic to the speakers would feed back.
  }

  /** Release the device and the audio graph. Safe to call more than once — the
   * mic indicator staying lit after a session ends is the failure this prevents. */
  stop(): void {
    this.stopped = true;
    this.worklet?.port.close();
    this.worklet?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    void this.context?.close().catch(() => undefined);
    if (this.moduleUrl) URL.revokeObjectURL(this.moduleUrl);
    this.worklet = null;
    this.source = null;
    this.stream = null;
    this.context = null;
    this.moduleUrl = null;
  }
}
